import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2, XIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogTitle } from '~/components/ui/dialog'
import { api } from '~/lib/tuyau'
import {
  ReapproCategoryGroupDialog,
  type ReapproCategoryGroupListItem,
} from './ReapproCategoryGroupDialog'

interface ReapproCategoryGroupsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCategoriesChanged: () => void
}

export const ReapproCategoryGroupsDialog = ({
  open,
  onOpenChange,
  onCategoriesChanged,
}: ReapproCategoryGroupsDialogProps) => {
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editedGroup, setEditedGroup] = useState<ReapproCategoryGroupListItem | undefined>()

  const { data: groups = [] } = useQuery(api.reapproCategoryGroups.index.queryOptions())

  const deleteMut = useMutation(
    api.reapproCategoryGroups.destroy.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: api.reapproCategoryGroups.index.queryOptions().queryKey,
        })
        onCategoriesChanged()
      },
      onError: () => toast.error('Erreur lors de la suppression du groupe'),
    }),
  )

  const openCreate = () => {
    setEditedGroup(undefined)
    setFormOpen(true)
  }

  const openEdit = (group: ReapproCategoryGroupListItem) => {
    setEditedGroup(group)
    setFormOpen(true)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-150 max-w-[calc(100%-2rem)] flex flex-col p-0 gap-0"
          showCloseButton={false}
        >
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
            <DialogTitle className="text-lg font-medium">Groupes de catégories</DialogTitle>
            <DialogClose render={<Button variant="ghost" size="icon-sm" />}>
              <XIcon />
              <span className="sr-only">Fermer</span>
            </DialogClose>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-4">
            <div className="flex justify-end">
              <Button onClick={openCreate}>
                <Plus />
                Nouveau groupe
              </Button>
            </div>

            {groups.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Aucun groupe pour le moment
              </p>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border/50">
                {groups.map((group) => (
                  <div key={group.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{group.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {group.maxProduction != null
                          ? `Max : ${group.maxProduction}`
                          : 'Sans limite'}
                        {' · '}
                        {group.reapproCategories.length} catégorie
                        {group.reapproCategories.length > 1 ? 's' : ''}
                      </p>
                    </div>
                    <Button variant="outline" size="icon-sm" onClick={() => openEdit(group)}>
                      <Pencil />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon-sm"
                      disabled={deleteMut.isPending}
                      onClick={() => deleteMut.mutate({ params: { id: group.id } })}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ReapproCategoryGroupDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        group={editedGroup}
        onSuccess={onCategoriesChanged}
      />
    </>
  )
}
